/**
 * Theme Hooks
 *
 * useThemeColors() — returns the full theme context value.
 * useThemedStyles() — creates memoized StyleSheet objects that update on theme change.
 */
import { useContext, useMemo } from 'react';
import { ThemeContext } from '@/contexts/shared/ThemeContext';
import type { ThemeContextValue } from '@/contexts/shared/ThemeContext';

export type { ThemeContextValue };

/**
 * Access the current theme's color tokens and mode.
 *
 * @example
 * const { colors, semantic, mode } = useThemeColors();
 * <Text style={{ color: colors.text.primary }}>{title}</Text>
 */
export function useThemeColors(): ThemeContextValue {
  return useContext(ThemeContext);
}

/**
 * Create a memoized stylesheet that recomputes only when the theme mode changes.
 *
 * @example
 * const styles = useThemedStyles(({ colors }) =>
 *   StyleSheet.create({
 *     title: { color: colors.text.primary },
 *     card: { backgroundColor: colors.card.bg },
 *   }),
 * );
 */
export function useThemedStyles<T>(factory: (theme: ThemeContextValue) => T): T {
  const theme = useContext(ThemeContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => factory(theme), [theme.mode]);
}
